/* ==========================================================================
   The SQL console

   Oster asked for somewhere to see the tables and write queries, from any
   device. This is that, inside the app, because the app is the only thing
   that can reach the database from a phone on a train: it is the same origin
   as the API and already knows who he is.

   Read-only is not enforced here. It is enforced by the server opening the
   database read-only, so a write is refused by SQLite itself rather than by
   anything this file remembered to check. What is here is the part that
   makes it usable: the schema beside the query, so you do not have to
   remember a column name to ask a question about it.
   ========================================================================== */

import { useEffect, useRef, useState } from 'react';
import { Card, Empty, SectionHead } from '../../components/ui';
import { api } from '../../api';

interface Column { name: string; type: string; pk: boolean }
interface Table { name: string; rows: number; columns: Column[] }
interface Result {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  ms: number;
}

const RECENT = 'ozeret.sql.recent';

/* A query handed over in the address -- /admin/sql#q=... -- from Oster's page
   of saved queries, so one tap there opens it here and runs it. Read once,
   as the app loads: the app rewrites the address as it routes, and the part
   after # would not survive that. It is never sent to the server in the
   address; it runs exactly as if it had been typed into the box. */
const LINKED: string | null = (() => {
  try {
    const m = /[#&]q=([^&]*)/.exec(window.location.hash);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
})();
let linkedUsed = false;

/* A handful to start from, because a blank box and 26 tables is not a good
   place to begin. These are the questions actually worth asking of a young
   marketplace. */
const STARTERS: [string, string][] = [
  ['Who signed up', 'SELECT created_at, role, name, phone\nFROM users\nORDER BY created_at DESC\nLIMIT 20'],
  ['Both sides', "SELECT role, COUNT(*) AS people FROM users GROUP BY role"],
  ['Requests nobody answered',
    "SELECT r.posted, r.date, h.display_name, r.rate\n"
    + "FROM requests r JOIN houses h ON h.id = r.house_id\n"
    + "WHERE r.status = 'open'\n"
    + "  AND NOT EXISTS (SELECT 1 FROM applicants a WHERE a.request_id = r.id)\n"
    + 'ORDER BY r.posted DESC'],
  ['Work booked', "SELECT date, status, rate, hours, rate * hours AS total\nFROM bookings\nORDER BY date DESC\nLIMIT 20"],
  ['Busiest tables', '-- what is actually in here\nSELECT 1'],
];

function useRecent() {
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT) ?? '[]') as string[]; } catch { return []; }
  });
  const remember = (sql: string) => {
    setRecent((was) => {
      const next = [sql, ...was.filter((q) => q !== sql)].slice(0, 12);
      try { localStorage.setItem(RECENT, JSON.stringify(next)); } catch { /* private window */ }
      return next;
    });
  };
  return { recent, remember };
}

export function Sql() {
  const [tables, setTables] = useState<Table[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [sql, setSql] = useState(LINKED && !linkedUsed ? LINKED : STARTERS[0][1]);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const { recent, remember } = useRecent();

  useEffect(() => {
    if (LINKED && !linkedUsed) {
      linkedUsed = true;
      void run(LINKED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.tables()
      .then((r) => setTables(r.tables))
      .catch((e) => setDenied(e?.message ?? 'Not available.'));
  }, []);

  const run = async (text: string = sql) => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.query(q);
      setResult(r);
      remember(q);
    } catch (e) {
      setError((e as Error)?.message ?? 'That did not run.');
    } finally {
      setBusy(false);
    }
  };

  /* Cmd/Ctrl+Enter runs it, which is what every other query tool does and
     the only shortcut worth having here. */
  const keys = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void run(); }
  };

  const use = (q: string) => {
    setSql(q);
    box.current?.focus();
  };

  if (denied) {
    return (
      <div className="screen-pad">
        <Empty big="Not available" sub={denied} />
      </div>
    );
  }

  return (
    <div className="screen-pad sqlpage">
      <Card>
        <SectionHead title="Query" />
        <textarea
          ref={box}
          className="textarea sqlbox"
          value={sql}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={keys}
          rows={5}
        />
        <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={() => void run()} disabled={busy}>
            {busy ? 'Running…' : 'Run'}
          </button>
          <span className="hint">
            Read-only — the database is opened read-only, so a write is refused.
          </span>
        </div>

        <div className="chips">
          {STARTERS.map(([label, q]) => (
            <button type="button" className="chip outline" key={label} onClick={() => use(q)}>
              {label}
            </button>
          ))}
        </div>
      </Card>

      {error && (
        <Card className="flat">
          <b className="sqlerr">{error}</b>
        </Card>
      )}

      {result && (
        <Card className="flat">
          <SectionHead title={
            result.total + (result.total === 1 ? ' row' : ' rows')
            + (result.truncated ? ' — showing the first 500' : '')
            + ' · ' + result.ms + 'ms'
          } />
          {result.rows.length === 0 ? (
            <span className="sub">Nothing matched.</span>
          ) : (
            <div className="sqlscroll">
              <table className="sqltable">
                <thead>
                  <tr>{result.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i}>
                      {result.columns.map((c) => (
                        <td key={c} className={r[c] === null ? 'nul' : undefined}>
                          {r[c] === null ? 'NULL' : String(r[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {recent.length > 0 && (
        <Card className="flat">
          <SectionHead title="Recent" />
          <div className="col" style={{ gap: 4 }}>
            {recent.map((q) => (
              <button type="button" className="sqlrecent" key={q} onClick={() => use(q)}>
                {q.replace(/\s+/g, ' ').slice(0, 90)}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="flat">
        <SectionHead title={(tables?.length ?? 0) + ' tables'} />
        {!tables ? (
          <span className="sub">Reading the schema…</span>
        ) : (
          <div className="col" style={{ gap: 2 }}>
            {tables.map((t) => (
              <div key={t.name}>
                <button
                  type="button"
                  className="sqltab"
                  onClick={() => setOpen(open === t.name ? null : t.name)}
                >
                  <b>{t.name}</b>
                  <span>{t.rows.toLocaleString()}</span>
                </button>
                {open === t.name && (
                  <div className="sqlcols">
                    {t.columns.map((c) => (
                      <button
                        type="button"
                        key={c.name}
                        className="sqlcol"
                        onClick={() => use('SELECT ' + c.name + ' FROM ' + t.name + '\nLIMIT 20')}
                        title={c.type || 'no declared type'}
                      >
                        {c.name}{c.pk ? ' ·' : ''}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="sqlcol all"
                      onClick={() => use('SELECT *\nFROM ' + t.name + '\nLIMIT 20')}
                    >
                      SELECT *
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
